import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { T, PageHeader, Section, Field, Alert } from '../components/UI';
import StaffDetailsFields, { EMPTY_DETAILS, validateDetails, todayISO, dateOnly } from '../components/StaffDetailsFields';

// Start date and email are only on this (manager) form — staff don't choose their own start date
const EMPTY = { startDate:'', email:'', ...EMPTY_DETAILS, signedDate:'' };

// What <LicenceUpload> needs to describe a file the browser is holding but hasn't uploaded yet
const pendingMeta = (p) => (p ? { uploaded:true, ext: p.file.type === 'application/pdf' ? 'pdf' : 'jpg', size: p.file.size, uploadedAt: p.stamp } : { uploaded:false });

// CS Staff → Add Staff / Edit Staff: the Employee Details form (same fields as staff self-registration)
export default function EmployeeForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editMode = !!id;

  const [form,    setForm]    = useState(EMPTY);
  const [staffId, setStaffId] = useState('');
  const [loading, setLoading] = useState(editMode);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(location.state?.notice || null);
  const [errors,  setErrors]  = useState({});
  // Edit mode: what's saved on the server. Add mode: files chosen but not uploaded until the person is saved.
  const [saved,   setSaved]   = useState({ licenceFront:{ uploaded:false }, licenceBack:{ uploaded:false } });
  const [pending, setPending] = useState({ front:null, back:null });

  useEffect(() => {
    if (!editMode) return;
    api.get(`/employees/${id}`)
      .then(({ data }) => {
        const e = data.employee;
        const loaded = { ...EMPTY };
        Object.keys(EMPTY).forEach(k => { if (e[k] !== undefined && e[k] !== null) loaded[k] = e[k]; });
        loaded.startDate     = dateOnly(e.startDate);
        loaded.dateOfBirth   = dateOnly(e.dateOfBirth);
        loaded.licenceExpiry = dateOnly(e.licenceExpiry);
        loaded.signedDate    = dateOnly(e.signedDate);
        setForm(loaded);
        setStaffId(e.staffId || '');
        setSaved({ licenceFront: e.licenceFront || { uploaded:false }, licenceBack: e.licenceBack || { uploaded:false } });
      })
      .catch(err => setError(err.response?.data?.message || 'Failed to load this staff member'))
      .finally(() => setLoading(false));
  }, [id, editMode]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: false })); };

  const validate = () => {
    const errs = validateDetails(form, editMode
      ? { front: saved.licenceFront.uploaded, back: saved.licenceBack.uploaded }
      : { front: !!pending.front, back: !!pending.back });
    ['startDate', 'email'].forEach(k => { if (!String(form[k]).trim()) errs[k] = true; });
    if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = 'Enter a valid email address';
    setErrors(errs);
    return errs;
  };

  // ── Licence photos ───────────────────────────────────────
  const sides = { front:'licenceFront', back:'licenceBack' };
  const licenceFor = (side) => {
    const key = sides[side];
    if (editMode) {
      return {
        meta: saved[key],
        fetchBlob: () => api.get(`/employees/${id}/licence/${side}`, { responseType:'blob' }).then(r => r.data),
        onUpload: async (file) => {
          const body = new FormData(); body.append('file', file);
          const { data } = await api.put(`/employees/${id}/licence/${side}`, body, { headers:{ 'Content-Type':'multipart/form-data' } });
          setSaved(s => ({ ...s, [key]: data[key] }));
          setErrors(e => ({ ...e, [key]: false }));
        },
        onRemove: async () => {
          const { data } = await api.delete(`/employees/${id}/licence/${side}`);
          setSaved(s => ({ ...s, [key]: data[key] }));
        },
      };
    }
    const p = pending[side];
    return {
      meta: pendingMeta(p),
      fetchBlob: async () => p.file,
      onUpload: async (file) => { setPending(x => ({ ...x, [side]: { file, stamp: `${file.name}-${file.size}-${Date.now()}` } })); setErrors(e => ({ ...e, [key]: false })); },
      onRemove: async () => setPending(x => ({ ...x, [side]: null })),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setError('Please fix the highlighted fields.');
      window.scrollTo({ top:0, behavior:'smooth' });
      return;
    }
    setSaving(true); setError(null);
    try {
      const payload = { ...form, signedDate: form.signature ? (form.signedDate || todayISO()) : '' };
      if (editMode) {
        await api.patch(`/employees/${id}`, payload);
        navigate('/staff');
        return;
      }
      const { data } = await api.post('/employees', payload);
      const newId = data.employee._id;
      // The person is saved — now upload the licence photos that were waiting
      try {
        for (const side of ['front', 'back']) {
          const body = new FormData(); body.append('file', pending[side].file);
          await api.put(`/employees/${newId}/licence/${side}`, body, { headers:{ 'Content-Type':'multipart/form-data' } });
        }
        navigate('/staff');
      } catch (uploadErr) {
        navigate(`/staff/${newId}/edit`, { state:{ notice: 'Staff member saved, but a licence photo could not be uploaded. Please upload it again below.' } });
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Could not save. Please try again.';
      setError(message);
      if (err.response?.status === 409) setErrors(x => ({ ...x, email: message }));
      window.scrollTo({ top:0, behavior:'smooth' });
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
    </div>
  );

  const input = (k, props = {}) => (
    <input value={form[k]} onChange={e=>set(k, e.target.value)} {...props}
      style={{ ...T.input, borderColor: errors[k] ? 'var(--red)' : 'var(--border-md)' }}/>
  );

  return (
    <div style={T.page}>
      <PageHeader title={editMode ? 'Edit Staff' : 'Add Staff'} onBack={()=>navigate('/staff')}/>

      <form onSubmit={handleSubmit} noValidate style={{ maxWidth:'700px', margin:'0 auto', padding:'20px 16px 120px' }}>
        {error && <Alert type="error">{error}</Alert>}

        <Section title="Information Recorded and Email" accent>
          <p style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'14px', lineHeight:1.5 }}>
            The email is used to send the staff member their induction login.
            {editMode ? '' : ' A unique staff ID (CS1, CS2, …) is created when you save.'}
          </p>
          {editMode && staffId && (
            <Field label="Staff ID">
              <input disabled value={staffId} style={{ ...T.input, background:'var(--bg)', color:'var(--blue)', fontFamily:'JetBrains Mono,monospace', fontWeight:'600', cursor:'not-allowed' }}/>
            </Field>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:'0 12px' }}>
            <Field label="Information recorded *" error={errors.startDate ? 'Required' : null}>{input('startDate', { type:'date' })}</Field>
            <Field label="Email *" error={errors.email ? (typeof errors.email === 'string' ? errors.email : 'Required') : null}>{input('email', { type:'email', placeholder:'name@example.com' })}</Field>
          </div>
        </Section>

        <StaffDetailsFields form={form} errors={errors} set={set}
          licenceNote="Licence photos are stored privately and can only be seen by Car Saloon management."
          licence={{ front: licenceFor('front'), back: licenceFor('back') }}/>

        <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'var(--surface)', borderTop:'1px solid var(--border)', padding:'12px 16px', paddingBottom:'calc(12px + env(safe-area-inset-bottom, 0px))', display:'flex', justifyContent:'flex-end', gap:'8px', zIndex:150 }}>
          <button type="button" onClick={()=>navigate('/staff')} style={T.btnSecondary}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...T.btnPrimary, opacity:saving?0.6:1 }}>
            <i className="ti ti-check" aria-hidden="true"/> {saving ? 'Saving...' : editMode ? 'Save Changes' : 'Add Staff'}
          </button>
        </div>
      </form>
    </div>
  );
}
